import { useState, useMemo } from 'react'
import { mockReviews } from '../data/mockReviews'
import { mockVendors } from '../data/mockVendors'
import ReviewCard from '../components/ReviewCard'
import ReviewForm from '../components/ReviewForm'
import { Review } from '../types'

export default function Reviews() {
  const [reviews, setReviews] = useState(mockReviews)
  const [showForm, setShowForm] = useState(false)
  const [filterVendor, setFilterVendor] = useState('')
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'highest' | 'lowest' | 'helpful'>('newest')
  const [filterRating, setFilterRating] = useState(0)

  const filteredAndSortedReviews = useMemo(() => {
    const filtered = reviews.filter((review: Review) => {
      const matchesVendor = !filterVendor || review.vendorId === filterVendor
      const matchesRating = review.rating >= filterRating
      return matchesVendor && matchesRating
    })

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
        case 'oldest':
          return new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime()
        case 'highest':
          return b.rating - a.rating
        case 'lowest':
          return a.rating - b.rating
        case 'helpful':
          return b.helpfulVotes - a.helpfulVotes
        default:
          return 0
      }
    })
  }, [reviews, filterVendor, sortBy, filterRating])

  const handleSubmitReview = (reviewData: {
    vendorId: string
    rating: number
    title: string
    content: string
    pros: string[]
    cons: string[]
    wouldRecommend: boolean
    productsPurchased: string[]
    userName: string
  }) => {
    const newReview: Review = {
      id: `review-${Date.now()}`,
      ...reviewData,
      dateCreated: new Date(),
      helpfulVotes: 0,
      verified: false,
      userId: `user-${Date.now()}`
    }
    setReviews(prev => [newReview, ...prev])
    setShowForm(false)
  }

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 0

  return (
    <div className="reviews-page">
      <div className="reviews-header">
        <h1>Vendor Reviews</h1>
        <p>Read honest reviews from the kratom community and share your own experiences.</p>
        <div className="reviews-stats">
          <div className="stat">
            <span className="stat-number">{reviews.length}</span>
            <span className="stat-label">Total Reviews</span>
          </div>
          <div className="stat">
            <span className="stat-number">{averageRating}</span>
            <span className="stat-label">Average Rating</span>
          </div>
          <div className="stat">
            <span className="stat-number">{mockVendors.length}</span>
            <span className="stat-label">Vendors Reviewed</span>
          </div>
        </div>
      </div>

      <div className="reviews-actions">
        <button 
          onClick={() => setShowForm(true)}
          className="write-review-btn"
        >
          ✍️ Write a Review
        </button>
      </div>

      <div className="reviews-filters">
        <div className="filter-group">
          <label htmlFor="vendor-filter">Filter by Vendor:</label>
          <select
            id="vendor-filter"
            value={filterVendor}
            onChange={(e) => setFilterVendor(e.target.value)}
            className="filter-select"
          >
            <option value="">All Vendors</option>
            {mockVendors.map(vendor => (
              <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="rating-filter">Minimum Rating:</label>
          <select
            id="rating-filter"
            value={filterRating}
            onChange={(e) => setFilterRating(Number(e.target.value))}
            className="filter-select"
          >
            <option value={0}>Any Rating</option>
            <option value={1}>1+ Stars</option>
            <option value={2}>2+ Stars</option>
            <option value={3}>3+ Stars</option>
            <option value={4}>4+ Stars</option>
            <option value={5}>5 Stars</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="sort-by">Sort by:</label>
          <select
            id="sort-by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'highest' | 'lowest' | 'helpful')}
            className="filter-select"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rated</option>
            <option value="lowest">Lowest Rated</option>
            <option value="helpful">Most Helpful</option>
          </select>
        </div>
      </div>

      <div className="reviews-summary">
        <p>Showing {filteredAndSortedReviews.length} of {reviews.length} reviews</p>
      </div>

      <div className="reviews-list">
        {filteredAndSortedReviews.map((review) => (
          <ReviewCard key={review.id} review={review} />
        ))}
      </div>

      {filteredAndSortedReviews.length === 0 && (
        <div className="no-reviews">
          <p>No reviews found matching your criteria. Try adjusting your filters.</p>
        </div>
      )}

      {showForm && (
        <ReviewForm
          onSubmit={handleSubmitReview}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  )
}